import { useState, useEffect } from 'react';
import { useHUser } from './hooks/HUsers';
import { useMessages } from './hooks/HMessages';
import GMessagesTable from '../tables/GMessagesTable';
import { Snackbar, Alert, TextField, Chip, Button } from '@mui/material';

export default function MessagesTab({ user }: { user?: any } = {}) {
    useEffect(() => { try { console.debug('[MessagesTab] mounted'); } catch (e) {} }, []);

    const { currentUser } = useHUser();
    const { messages, loading, error, loadAllMessages, loadMessagesByUser, loadMessagesByOwner } = useMessages();

    const [searchInput, setSearchInput] = useState('');

    const [sentMessages, setSentMessages] = useState<any[]>([]);
    const [receivedMessages, setReceivedMessages] = useState<any[]>([]);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        const uid = user?.id ?? user?.user_id ?? currentUser?.id ?? currentUser?.user_id ?? null;
        if (!uid) return;

        (async () => {
            // Load general cache (optional) and then request server-side filtered messages for this user
            try { console.debug('[MessagesTab] loadAllMessages for uid:', uid); } catch (e) {}
            await loadAllMessages();
            try { console.debug('[MessagesTab] loadMessagesByUser for uid:', uid); } catch (e) {}
            const userMsgsResult = await loadMessagesByUser(uid);
            const userMsgs = Array.isArray(userMsgsResult) ? userMsgsResult : [];

            const sent = userMsgs.filter((m: any) => String(m.user ?? m.user_id ?? m.sender_id ?? m.sender ?? '') === String(uid));

            setSentMessages(sent);

            // Use server-side owner filtering for received messages
            try {
                const ownerMsgsResult = await loadMessagesByOwner(user?.company);
                const ownerMsgs = Array.isArray(ownerMsgsResult) ? ownerMsgsResult : [];
                setReceivedMessages(ownerMsgs);
            } catch (e) {
                console.warn('[MessagesTab] loadMessagesByOwner failed:', e);
                setReceivedMessages([]);
            }
        })();
    // Note: do NOT include `loadMessagesByUser` in deps because its identity
    // changes whenever `messages` updates inside the hook, which would
    // retrigger this effect and cause an infinite fetch loop (see RequestsTab).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, currentUser, loadAllMessages]);

    useEffect(() => {
        if (!searchInput) return; // if empty, keep original lists
        const q = searchInput.trim().toLowerCase();
        if (!q) return;

        const filterFn = (m: any) => {
            const fields = [m.subject, m.body, m.to, m.from, m.user, m.receiver, m.sender, m.owner];
            return fields.some((f: any) => String(f ?? '').toLowerCase().includes(q));
        };

        setSentMessages((prev) => (Array.isArray(prev) ? prev.filter(filterFn) : prev));
        setReceivedMessages((prev) => (Array.isArray(prev) ? prev.filter(filterFn) : prev));
    }, [searchInput]);

    return (
        <div className='messages-page'>
            <div className='standard-div'>
                <div className='standard-div-flex'>
                    <div className='deals-title-box'>
                        <h1 style={{ margin: 0 }}>Messages</h1>
                        {!loading && (
                            <Chip label={`${(messages ?? []).length} message${(messages ?? []).length !== 1 ? 's' : ''}`} size='small' color='primary' variant='outlined' />
                        )}
                    </div>
                </div>
                <div className='header-flex'>
                    <TextField size='small' placeholder='Search messages...' value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className='search-field' />
                    <Button variant='contained' className='gold-button' onClick={() => setSearchInput('')}>Clear</Button>
                </div>
            </div>

            <div className='standard-div'>
                <h2>Messages Sent</h2>
            </div>
            <GMessagesTable messages={sentMessages} loading={loading} error={error} />

            <div style={{ padding: '12px 0' }}>
                <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '0 0 12px' }} />
            </div>

            <div className='standard-div'>
                <h2>Messages Received</h2>
            </div>
            <GMessagesTable messages={receivedMessages} loading={loading} error={error} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
